import type { RxDocument } from 'rxdb';

import { ContextItem, type ContextItemProps, type ManagedSyncStatus } from '@/components/context-item';
import { SourceInput } from '@/components/source-input';
import { getSourceDocument, type SourceSearchItem } from '@/hooks/use-source-list-model';
import { isMediaSourceType } from '@/utils/source-preview';

import type { UserSource, UserSourceAvailabilityScope } from '@flintmint/core-shared/rxdb';

export type LocalSourceRowItem = {
  kind: 'uploading';
  id: string;
  name: string;
  sourceType?: string | null;
  status: 'uploading' | 'error';
  progress?: number;
  error?: string | null;
};

export type SourceRowItem = SourceSearchItem | LocalSourceRowItem;

export const isLocalSourceRowItem = (item: SourceRowItem): item is LocalSourceRowItem => {
  return typeof item === 'object' && item !== null && 'kind' in item;
};

export const SourceRow = (props: SourceRowProps) => {
  const { contextItemProps, availabilityScope } = isLocalSourceRowItem(props.item)
    ? buildLocalItemProps(props.item, props)
    : buildPersistedItemProps(props.item, props);

  // Note-scoped sources can't be detached (it would orphan them), only deleted.
  // Callers may wire onDetachFromNote unconditionally, so we strip it here.
  if (availabilityScope === 'note') {
    contextItemProps.onDetachFromNote = undefined;
  }

  return <ContextItem {...contextItemProps} />;
};

type BuiltContextItemProps = {
  contextItemProps: ContextItemProps;
  availabilityScope?: UserSourceAvailabilityScope;
};

const buildLocalItemProps = (item: LocalSourceRowItem, props: SourceRowProps): BuiltContextItemProps => {
  const { appearance, onRemoveLocalItem, getSharedContextItemProps } = props;

  return {
    availabilityScope: undefined,
    contextItemProps: {
      id: item.id,
      name: item.name,
      sourceType: item.sourceType,
      source: undefined,
      appearance,
      onDelete: () => onRemoveLocalItem?.(item.id),
      ...(getSharedContextItemProps?.(item.id, item.name, item.sourceType, undefined, true, undefined) ?? {}),
      uploadStatus: item.status,
      uploadProgress: item.progress,
      error: item.error,
    },
  };
};

const buildPersistedItemProps = (item: SourceSearchItem, props: SourceRowProps): BuiltContextItemProps => {
  const {
    onDetach,
    appearance,
    toggleAttachment,
    onOpenAudioPreview,
    showSourceInputMeta,
    persistedExtraProps,
    noteUserSources = {},
    canToggleEnabled = false,
    highlightAttached = false,
    onUserSourceEnabledChange,
    getSharedContextItemProps,
    canToggleAttachment = false,
  } = props;

  const sourceDoc = getSourceDocument(item);
  const sourceId = sourceDoc.id;
  const sourceAttachment = noteUserSources[sourceId];
  const sourceAttached = !!sourceAttachment;
  const sourceEnabled = sourceAttachment?.enabled ?? false;
  const availabilityScope = sourceDoc.availabilityScope as UserSourceAvailabilityScope | undefined;

  const sharedProps = getSharedContextItemProps?.(
    sourceId,
    sourceDoc.name,
    sourceDoc.sourceType,
    sourceDoc.source,
    false,
    availabilityScope
  ) ?? {
    onDetachFromNote: sourceAttached && onDetach ? () => onDetach(sourceId) : undefined,
  };

  const extraProps = persistedExtraProps?.({ item, sourceDoc, sourceId, sourceAttached, sourceEnabled }) ?? {};

  return {
    availabilityScope,
    contextItemProps: {
      id: sourceId,
      name: sourceDoc.name,
      error: sourceDoc.error,
      appearance,
      sourceType: sourceDoc.sourceType,
      source: sourceDoc.source,
      enabled: sourceEnabled,
      attached: sourceAttached,
      onToggleEnabled: canToggleEnabled ? () => onUserSourceEnabledChange?.(sourceId, !sourceEnabled) : undefined,
      onToggleAttachment: canToggleAttachment ? () => toggleAttachment?.(sourceId, sourceAttached) : undefined,
      openAudioInAModal: () => onOpenAudioPreview?.(sourceId),
      parsedContent: (sourceDoc as UserSource).parsedContent ?? sourceDoc.content ?? '',
      updatedAt: sourceDoc.updatedAt as string,
      syncStatus: sourceDoc.syncStatus as ManagedSyncStatus,
      highlightAttached: highlightAttached && sourceAttached,
      ...sharedProps,
      ...extraProps,
      meta:
        showSourceInputMeta && !isMediaSourceType(sourceDoc.sourceType) ? (
          <SourceInput userSource={sourceDoc as unknown as RxDocument<UserSource>} />
        ) : undefined,
    },
  };
};

interface SourceRowProps {
  item: SourceRowItem;
  canToggleEnabled?: boolean;
  highlightAttached?: boolean;
  showSourceInputMeta?: boolean;
  canToggleAttachment?: boolean;
  onDetach?: (id: string) => void;
  appearance?: 'default' | 'compact';
  onRemoveLocalItem?: (id: string) => void;
  onOpenAudioPreview?: (id: string) => void;
  toggleAttachment?: (sourceId: string, attached: boolean) => void;
  noteUserSources?: Record<string, { index?: number; enabled?: boolean }>;
  onUserSourceEnabledChange?: (userSourceId: string, enabled: boolean) => void;
  getSharedContextItemProps?: (
    id: string,
    name: string,
    sourceType?: string | null,
    source?: string | null,
    uploading?: boolean,
    availabilityScope?: UserSourceAvailabilityScope
  ) => Partial<ContextItemProps>;
  persistedExtraProps?: (args: {
    item: SourceSearchItem;
    sourceDoc: ReturnType<typeof getSourceDocument>;
    sourceId: string;
    sourceAttached: boolean;
    sourceEnabled: boolean;
  }) => Partial<ContextItemProps>;
}
